import React, { forwardRef, Ref } from 'react';

import * as Sentry from '@sentry/react';
import { ClientOnly } from '@wac-web/utils/client-only';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes, Link, LinkProps } from 'react-router-dom';

import AppProvider, { type RouterLinkComponentProps } from '@atlaskit/app-provider';

import { FourOhFourDisplay } from '@/common/displays';
import { GenericErrorDisplay } from '@/common/displays';
import { PageLayout } from '@/common/page-layout/src/page-layout';
import { FASTSHIFT_PATHS, FASTSHIFT_PATH_KEYS } from '@/common/routes/constants';
import { beforeCaptureWithScope } from '@/common/utils/sentry-with-scope';
import Flag from '@/contexts/FlagProvider';
import { FastshiftCsProfilerPage } from '@/pages/cs-profiler/fastshift-cs-profiler-page';
import { FastshiftCustomerSelectionPage } from '@/pages/customer-selection';
import { FastshiftDashboard } from '@/pages/dashboard';
import { FastshiftDcInstanceDetailsPage } from '@/pages/dc-instance-details/fastshift-dc-instance-details-page';
import { FastshiftPage } from '@/pages/fastshift-landing';
import { FastshiftMigrationTicketsPage } from '@/pages/migration-tickets';
import { FastshiftMigrationsManagementPage } from '@/pages/migrations-management-page';
import { FastshiftOrganizationDetailsPage } from '@/pages/organization-details/fastshift-organization-details-page';
import { FastshiftOrganizationsListPage } from '@/pages/organizations-list/src/fastshift-organizations-list-page';
import { FastshiftResourcesPage } from '@/pages/resources-page';
import { store as FastShiftPortalStore } from '@/state/store';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { RedirectToSelectionPage } from './redirect-to-selection-page';

const CustomRouterLink = forwardRef(
	(
		{ href, children, ...rest }: RouterLinkComponentProps<LinkProps>,
		ref: Ref<HTMLAnchorElement>,
	) => {
		// A basic link by passing a string as the component's `href` prop.
		if (typeof href === 'string') {
			return (
				<Link ref={ref} to={href} {...rest}>
					{children}
				</Link>
			);
		}

		// Advanced link configuration by passing an object as the
		// component's `href` prop
		return (
			<Link
				ref={ref}
				// href={href.href}
				to={href.to}
				replace={href.replace}
				{...rest}
			>
				{children}
			</Link>
		);
	},
);

export const FastshiftPortalPageRouter: React.FC = () => {
	const isCsProfilerToolsMenuEnabled = checkFeatureFlagGate(featureFlags.sxpFspToolsMenuCsProfiler);

	return (
		<ClientOnly>
			<Sentry.ErrorBoundary
				beforeCapture={beforeCaptureWithScope('catch-all')}
				fallback={<GenericErrorDisplay />}
			>
				<AppProvider routerLinkComponent={CustomRouterLink}>
					<BrowserRouter>
						<Flag.Provider>
							<Provider store={FastShiftPortalStore}>
								<style
									dangerouslySetInnerHTML={{
										__html: `.grecaptcha-badge { visibility: hidden !important; }`,
									}}
								/>

								<Routes>
									{/* Layout route keeps PageLayout mounted for all fastshift routes */}
									<Route path="/:locale?">
										<Route element={<PageLayout />}>
											{/* Selection */}
											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.SELECTION]}
												element={<FastshiftCustomerSelectionPage />}
											/>
											{/* Domain-scoped pages */}
											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.OVERVIEW]}
												element={<FastshiftDashboard />}
											/>
											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.ORGANIZATIONS]}
												element={<FastshiftOrganizationsListPage />}
											/>
											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.DC_INSTANCE_DETAILS]}
												element={<FastshiftDcInstanceDetailsPage />}
											/>
											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.ORGANIZATION_DETAILS]}
												element={<FastshiftOrganizationDetailsPage />}
											/>
											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.TICKETS]}
												element={<FastshiftMigrationTicketsPage />}
											/>
											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.PLANNING]}
												element={<FastshiftMigrationsManagementPage />}
											/>
											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.RESOURCES_DOMAIN_SCOPED]}
												element={<FastshiftResourcesPage />}
											/>
											{/* Partner-scoped resources without domain */}
											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.RESOURCES_PARTNER_SCOPED]}
												element={<FastshiftResourcesPage />}
											/>
											{isCsProfilerToolsMenuEnabled && (
												<Route
													path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.CS_PROFILER]}
													element={<FastshiftCsProfilerPage />}
												/>
											)}

											<Route
												path={FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.FASTSHIFT]}
												element={<FastshiftPage />}
											/>

											{/* Legacy redirect */}
											<Route path="fastshift/dashboard" element={<RedirectToSelectionPage />} />

											{/* Catch all */}
											<Route path="fastshift/*" element={<FourOhFourDisplay />} />
										</Route>
									</Route>
								</Routes>
							</Provider>
						</Flag.Provider>
					</BrowserRouter>
				</AppProvider>
			</Sentry.ErrorBoundary>
		</ClientOnly>
	);
};
